// error-log.ts — lightweight in-app error log so production issues are visible
// proactively (instead of being discovered by chance). Stores the last N errors
// in localStorage; surfaced in Settings → "Nhật ký lỗi" to view/copy/clear.
// No backend, no PII upload — device-local only.
/* global ErrorEvent, PromiseRejectionEvent */

export interface ErrorLogEntry {
  at: string; // ISO timestamp
  source: string; // "window" | "promise" | "sync" | "ai" | ...
  message: string;
  detail?: string; // stack / extra context (truncated)
}

const KEY = "personal-life-os:error-log";
const MAX = 50;

function read(): ErrorLogEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ErrorLogEntry[]) : [];
  } catch {
    return [];
  }
}

/** Append an error (newest first), capped at MAX. Never throws. */
export function logError(source: string, message: unknown, detail?: unknown): void {
  try {
    const entry: ErrorLogEntry = {
      at: new Date().toISOString(),
      source,
      message: String(message ?? "").slice(0, 500) || "(không rõ)",
      ...(detail ? { detail: String(detail).slice(0, 1500) } : {}),
    };
    const next = [entry, ...read()].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Quota / private mode — ignore.
  }
}

export function getErrorLog(): ErrorLogEntry[] {
  return read();
}

export function clearErrorLog(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

let installed = false;

/** Install global window error + unhandled-rejection handlers (idempotent). */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    const err = (e as ErrorEvent).error as Error | undefined;
    logError("window", (e as ErrorEvent).message || err?.message, err?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason as { message?: string; stack?: string } | string | undefined;
    const msg = typeof reason === "string" ? reason : reason?.message;
    const stack = typeof reason === "object" ? reason?.stack : undefined;
    logError("promise", msg, stack);
  });
}
